import {
  GetApPayeeInfoResponse,
  GetApPayeeInfoResponseInvoiceReceiveEmailProcessType,
} from 'ap-openapi';
import { TFunction } from 'i18next';
import { PAYEE_ID_PARAM } from '../constants';
import { InvoiceReceiveEmailProcessType } from '../types';

type GetErrorMessageParams = {
  t: TFunction;
  statusCode: number;
  errorMessages?: string[];
  defaultKey?: string;
};

const ERROR_STATUS_CODE_MAPPING = new Map<number, string>([
  [404, 'ap_payees_detail_drawer.error.not_found'],
  [422, 'ap_payees_detail_drawer.update.failed'],
]);

/**
 * Get error message with priority:
 * 1. Translation key with status code (e.g., `ap_payees_detail_drawer.update.403_error`)
 * 2. Error messages from response (joined with ', ')
 * 3. Default/fallback translation key
 */
const getErrorMessage = ({
  t,
  statusCode,
  errorMessages,
  defaultKey = 'system_error',
}: GetErrorMessageParams): string => {
  // Check if status code specific translation exists
  const translationKey = ERROR_STATUS_CODE_MAPPING.get(statusCode);
  if (translationKey) {
    return t(translationKey);
  }

  // Check if error messages exist in response
  if (errorMessages?.length) {
    return errorMessages.join(', ');
  }

  // Return default message
  return t(defaultKey);
};

/**
 * Maps API InvoiceReceiveEmailProcessType to domain type
 */
const mapApiProcessTypeToDomain = (
  apiType?: GetApPayeeInfoResponseInvoiceReceiveEmailProcessType
): InvoiceReceiveEmailProcessType | undefined => {
  if (!apiType) return undefined;
  switch (apiType) {
    case GetApPayeeInfoResponseInvoiceReceiveEmailProcessType.payment_request:
      return InvoiceReceiveEmailProcessType.PAYMENT_REQUEST;
    case GetApPayeeInfoResponseInvoiceReceiveEmailProcessType.received_invoice:
      return InvoiceReceiveEmailProcessType.RECEIVED_INVOICE;
    default:
      return undefined;
  }
};

export const GetMasterData = (data: GetApPayeeInfoResponse) => {
  return {
    generalInformation: GetGeneralInformationData(data),
    PICAndContractDetail: GetPICAndContractDetailData(data),
    receivingMethod: getReceivingMethodData(data),
    reconciliationSetting: GetReconciliationSettingData(data),
    apAiServiceSettings: data.ap_ai_service_settings,
    paymentRule: GetPaymentRuleData(data),
    transactionDefaultValue: GetTransactionDefaultValueData(data),
    isEditable: data?.is_editable ?? false,
  };
};

const GetGeneralInformationData = (data: GetApPayeeInfoResponse) => {
  return {
    id: data?.id,
    display_name: data?.display_name,
  };
};

const getWebReceivingMethodData = (data: GetApPayeeInfoResponse) => {
  return {
    supplierUploadSetting: data?.supplier_upload_setting ?? undefined,
    is_supplier_upload_setting_enabled:
      data?.is_supplier_upload_setting_enabled,
    is_payee_active: data?.is_active,
  };
};

const getProxyReceiptReceivingMethodData = (data: GetApPayeeInfoResponse) => {
  return {
    is_proxy_receipt_receiving_method_enabled:
      data.is_agent_receive_setting_enabled,
    agentReceiveSetting: data.agent_receive_setting,
  };
};

const getEmailReceivingMethodData = (data: GetApPayeeInfoResponse) => {
  return {
    is_invoice_receive_email_setting_existed:
      data.is_invoice_receive_email_setting_existed,
    invoice_receive_email_address:
      data.invoice_receive_email_address ?? undefined,
    invoice_receive_email_process_type: mapApiProcessTypeToDomain(
      data.invoice_receive_email_process_type
    ),
    contact_office_member: data.contact_office_member,
    is_invoice_receive_mail_address_issued:
      data.is_invoice_receive_mail_address_issued,
  };
};

const getReceivingMethodData = (data: GetApPayeeInfoResponse) => {
  return {
    id: data?.id,
    webReceivingMethod: getWebReceivingMethodData(data),
    proxyReceivingMethod: getProxyReceiptReceivingMethodData(data),
    emailReceivingMethod: getEmailReceivingMethodData(data),
  };
};

const GetPICAndContractDetailData = (data: GetApPayeeInfoResponse) => {
  return {
    id: data?.id,
    ap_payee_contact_setting: data?.ap_payee_contact_setting,
    contact_office_members: data?.contact_office_members,
    contact_person_email: data?.contact_person_email,
    contact_person_full_address: data?.contact_person_full_address,
    contact_person_name: data?.contact_person_name,
    contact_person_phone_number: data?.contact_person_phone_number,
    dept_name: data?.dept_name,
  };
};

const GetPaymentRuleData = (data: GetApPayeeInfoResponse) => {
  return {
    payment_closing_date: data?.payment_closing_date,
    payment_month: data?.payment_month,
    payment_due_date: data?.payment_due_date,
    payment_holiday_rule: data?.payment_holiday_rule,
  };
};

const GetTransactionDefaultValueData = (data: GetApPayeeInfoResponse) => {
  return data?.ap_invoice_transaction_default_value;
};

/**
 * `ap_payee_reconciliation_setting` stays null while the office has not enabled
 * 突合サービス連携, which is what hides the section and its anchor item.
 */
const GetReconciliationSettingData = (data: GetApPayeeInfoResponse) => {
  return {
    id: data?.id,
    ap_payee_reconciliation_setting:
      data?.ap_payee_reconciliation_setting ?? undefined,
  };
};

const GetNullDataMaster = () => {
  return {
    generalInformation: { id: '', display_name: '' },
    PICAndContractDetail: {
      id: '',
      ap_payee_contact_setting: undefined,
      contact_office_members: undefined,
      contact_person_email: undefined,
      contact_person_full_address: undefined,
      contact_person_name: undefined,
      contact_person_phone_number: undefined,
      dept_name: undefined,
    },
    receivingMethod: {
      id: '',
      webReceivingMethod: {
        supplierUploadSetting: undefined,
        is_supplier_upload_setting_enabled: undefined,
        is_payee_active: undefined,
      },
      proxyReceivingMethod: {
        is_proxy_receipt_receiving_method_enabled: undefined,
        agentReceiveSetting: undefined,
      },
      emailReceivingMethod: {
        is_invoice_receive_email_setting_existed: undefined,
        invoice_receive_email_address: undefined,
        invoice_receive_email_process_type: undefined,
        contact_office_member: undefined,
        is_invoice_receive_mail_address_issued: undefined,
      },
    },
    reconciliationSetting: {
      id: '',
      ap_payee_reconciliation_setting: undefined,
    },
    apAiServiceSettings: [],
    paymentRule: {
      payment_closing_date: undefined,
      payment_month: undefined,
      payment_due_date: undefined,
      payment_holiday_rule: undefined,
    },
    transactionDefaultValue: undefined,
    isEditable: false,
  };
};

/**
 * Navigate to payee list page with current search conditions
 * Resets paging to page 1 and removes id from URL
 */
const navigateToPayeeList = (onClose?: () => void): void => {
  const url = new URL(window.location.href);
  onClose?.();
  // Remove page & payee_id from search params
  ['page', PAYEE_ID_PARAM].forEach((key) => url.searchParams.delete(key));
  // Redirect to payee list page
  window.location.replace(url.pathname + url.search);
};

/**
 * Close drawer, set toast message to sessionStorage, and navigate to payee list page
 */
const navigateToListWithMessage = (
  messageText: string,
  messageKey: string,
  onClose?: () => void
): void => {
  onClose?.();
  sessionStorage.setItem(messageKey, messageText);
  navigateToPayeeList();
};

export {
  getErrorMessage,
  GetGeneralInformationData,
  GetNullDataMaster,
  GetPaymentRuleData,
  GetPICAndContractDetailData,
  GetReconciliationSettingData,
  GetTransactionDefaultValueData,
  navigateToListWithMessage,
  navigateToPayeeList,
};
